import { forwardRef } from 'react';
import { Box, Stack, Typography, Chip, Button, Tooltip } from '@mui/material';
import SouthIcon from '@mui/icons-material/South';
import FlagIcon from '@mui/icons-material/Flag';
import ScoreControl from './ScoreControl';
import QuestionTooltip from '../QuestionTooltip';
import { level as levelTokens } from '../../theme/tokens';

/**
 * One question: the prompt, its level, and both scores side by side.
 *
 * `isConcernDomain` covers Sensory and Behaviour, where a Yes/Yes is not an
 * achievement but a flag that feeds the report's Concerns section — so it gets
 * an explicit marker rather than being inferred from a colour.
 */
const QuestionRow = forwardRef(({
  question,
  number,
  level,
  value = {},
  cursorField,
  isConcernDomain,
  onScore,
  onMirror,
  onFocusField,
}, ref) => {
  const tone = level ? levelTokens[level] : null;
  const answered = value.answer !== undefined && value.pr !== undefined;
  const partial = !answered && (value.answer !== undefined || value.pr !== undefined);
  const flagged = isConcernDomain && value.answer === 2 && value.pr === 2;
  const canMirror = value.answer !== undefined && value.pr !== value.answer;

  return (
    <Box
      ref={ref}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 1,
        border: '1px solid',
        borderColor: cursorField ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
        boxShadow: cursorField ? 1 : 0,
        scrollMarginTop: 180,
        transition: (t) => `border-color ${t.transitions.duration.shortest}ms, box-shadow ${t.transitions.duration.shortest}ms`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={2}>
        <Box
          sx={{
            minWidth: 30, height: 30, borderRadius: 1, flexShrink: 0,
            display: 'grid', placeItems: 'center',
            bgcolor: answered ? 'success.light' : partial ? 'warning.light' : 'grey.100',
            color: answered ? 'success.dark' : partial ? 'warning.dark' : 'text.secondary',
            fontSize: 13, fontWeight: 700,
          }}
        >
          {number}
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body1">
            {question.text}
            {question.description && <QuestionTooltip description={question.description} />}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          {flagged && (
            <Tooltip title="Both observed — this becomes a concern in the report">
              <Chip size="small" icon={<FlagIcon />} color="warning" label="Concern" />
            </Tooltip>
          )}
          {tone && (
            <Tooltip title={tone.label}>
              <Chip
                size="small"
                label={`L${level}`}
                sx={{ bgcolor: tone.light, color: tone.main, border: '1px solid', borderColor: tone.main }}
              />
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-end' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ScoreControl
            label="Clinician"
            value={value.answer}
            active={cursorField === 'answer'}
            onChange={(v) => onScore('answer', v)}
            onFocus={() => onFocusField('answer')}
          />
        </Box>

        {/* Agreement is the common case; copying the clinician's answer down is
            one tap instead of three. */}
        <Tooltip title="Copy the clinician's answer to the parent">
          <span>
            <Button
              size="small"
              onClick={onMirror}
              disabled={!canMirror}
              sx={{
                minWidth: 40, alignSelf: { xs: 'flex-start', sm: 'flex-end' },
                mb: { sm: 0.25 },
                transform: { xs: 'rotate(0deg)', sm: 'rotate(-90deg)' },
              }}
              aria-label="Copy clinician answer to parent"
            >
              <SouthIcon fontSize="small" />
            </Button>
          </span>
        </Tooltip>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ScoreControl
            label="Parent"
            value={value.pr}
            active={cursorField === 'pr'}
            onChange={(v) => onScore('pr', v)}
            onFocus={() => onFocusField('pr')}
          />
        </Box>
      </Stack>
    </Box>
  );
});

QuestionRow.displayName = 'QuestionRow';

export default QuestionRow;
